import { Auth0Provider } from "@auth0/auth0-react";
import { Toaster } from "@choose/ui/sonner";
import { TooltipProvider } from "@choose/ui/tooltip";

import "~/libs/i18n";
import "~/libs/instrument";

import "~/styles.css";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { AppUpdater } from "~/components/layout/app-updater";
import { ThemeProvider } from "~/components/layout/theme-provider";
import { page } from "~/libs/analytics";
import { createRouter, queryClient } from "~/router";

const router = createRouter();

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <Auth0Provider
        domain={import.meta.env.VITE_SUPPORT_AUTH0_DOMAIN as string}
        clientId={import.meta.env.VITE_SUPPORT_AUTH0_CLIENT_ID as string}
        authorizationParams={{
          audience: `${import.meta.env.VITE_SUPPORT_AUTH0_AUDIENCE as string}`,
          redirect_uri: `${window.location.origin}/auth-redirect`,
          scope: "openid profile email offline_access",
        }}
      >
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <RouterProvider router={router} />
              <Toaster />
              <AppUpdater />
            </TooltipProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </Auth0Provider>
    </StrictMode>,
  );
}

router.history.subscribe((url) => {
  const pathname = url.location.pathname;

  if (pathname === "/auth-redirect") {
    page("AuthRedirect");
    return;
  }

  if (pathname === "/contact-redirect") {
    page("ContactRedirect");
    return;
  }

  if (pathname.startsWith("/contact/")) {
    page("Contact");
    return;
  }

  if (pathname.startsWith("/order/")) {
    page("Order");
    return;
  }

  if (pathname.startsWith("/sale/")) {
    page("Sale");
    return;
  }

  if (pathname.startsWith("/ticket/")) {
    page("Ticket");
    return;
  }

  if (pathname === "/tickets/all") {
    page("TicketsAll");
    return;
  }

  if (pathname === "/tickets/yours") {
    page("TicketsYours");
    return;
  }

  if (pathname === "/tickets/unassigned") {
    page("TicketsUnassigned");
    return;
  }

  if (pathname === "/tickets/todo") {
    page("TicketTodo");
    return;
  }

  if (pathname === "/tickets/needs-first-response") {
    page("TicketsNeedsFirstResponse");
    return;
  }

  if (pathname === "/tickets/needs-next-response") {
    page("TicketsNeedsNextResponse");
    return;
  }

  if (pathname === "/tickets/investigating") {
    page("TicketsInvestigating");
    return;
  }

  if (pathname === "/tickets/snoozed") {
    page("TicketsSnoozed");
    return;
  }

  if (pathname === "/tickets/waiting-for-contact") {
    page("TicketsWaitingForContact");
    return;
  }

  if (pathname === "/tickets/waiting-for-supplier") {
    page("TicketsWaitingForSupplier");
    return;
  }

  if (pathname === "/tickets/paused-for-later") {
    page("TicketsPausedForLater");
    return;
  }

  if (pathname === "/tickets/done") {
    page("TicketsDone");
    return;
  }

  if (pathname === "/views/add") {
    page("ViewAdd");
    return;
  }

  if (pathname === "/views") {
    page("Views");
    return;
  }

  if (pathname.startsWith("/view/")) {
    page("View");
    return;
  }

  if (pathname === "/search") {
    page("Search");
    return;
  }

  if (pathname === "/settings/preferences") {
    page("SettingsPreferences");
    return;
  }

  if (pathname === "/settings/profile") {
    page("SettingsProfile");
    return;
  }

  if (pathname === "/settings/members") {
    page("SettingsMembers");
    return;
  }

  if (pathname.startsWith("/settings/member/")) {
    page("SettingsMember");
    return;
  }

  if (pathname === "/settings/snippet-tags/add") {
    page("SettingsSnippetTagAdd");
    return;
  }

  if (pathname === "/settings/snippet-tags") {
    page("SettingsSnippetTags");
    return;
  }

  if (pathname.startsWith("/settings/snippet-tag/")) {
    page("SettingsSnippetTag");
    return;
  }

  if (pathname === "/settings/snippets/add") {
    page("SettingsSnippetAdd");
    return;
  }

  if (pathname === "/settings/snippets") {
    page("SettingsSnippets");
    return;
  }

  if (pathname.startsWith("/settings/snippet/")) {
    page("SettingsSnippet");
    return;
  }

  if (pathname === "/settings") {
    page("Settings");
    return;
  }

  if (pathname === "/") {
    page("Home");
    return;
  }

  page(pathname);
});
